import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Play, ChevronRight, LayoutGrid, Package, CalendarClock, Sparkles } from "lucide-react";
import { api, apiError } from "../api";
import { PageLoader, EmptyState, StatusBadge, Spinner, Toast, useToast } from "../components/ui";

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function scheduleText(p) {
  if (!p.weekdays || p.weekdays.length === 0) return "No schedule";
  const hh = String(p.scan_hour).padStart(2, "0");
  const mm = String(p.scan_minute).padStart(2, "0");
  const days = p.weekdays.length === 7 ? "Daily" : (p.weekdays || []).map((d) => WEEKDAY_LABELS[d]).join(", ");
  return `${days} · ${hh}:${mm}`;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { toast, show, close } = useToast();
  const [data, setData] = useState(null);
  const [running, setRunning] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await api.get("/dashboard");
      setData(res.data);
    } catch (e) {
      show(apiError(e), "error");
    }
  }, [show]);

  useEffect(() => {
    load();
  }, [load]);

  const runNow = async (id) => {
    setRunning((r) => ({ ...r, [id]: true }));
    try {
      const { data: res } = await api.post(`/profiles/${id}/scan`, { trigger: "manual" });
      show("Scan started. Results will appear shortly.");
      navigate(`/scans/${res.scan.id}`);
    } catch (e) {
      show(apiError(e), "error");
    } finally {
      setRunning((r) => ({ ...r, [id]: false }));
    }
  };

  if (!data) return <PageLoader label="Loading dashboard" />;

  return (
    <div className="fade-in">
      <Toast toast={toast} onClose={close} />
      {data.subscription_status === "trialing" && data.has_access && (
        <div className="card p-3 mb-4 bg-teal-light border-teal/20 flex items-center gap-2" data-testid="dashboard-trial-banner">
          <Sparkles className="w-4 h-4 text-teal shrink-0" />
          <span className="text-sm text-teal flex-1">
            Free trial — {data.trial_days_left} day{data.trial_days_left === 1 ? "" : "s"} left.
          </span>
          <button className="text-sm font-medium text-teal underline" onClick={() => navigate("/pricing")} data-testid="dashboard-subscribe">
            Subscribe
          </button>
        </div>
      )}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl md:text-3xl text-ink">Monitoring profiles</h1>
          <p className="text-sm text-muted mt-1">
            {data.profiles_used} of {data.max_profiles} profiles used
          </p>
        </div>
        <button
          className="btn-accent"
          onClick={() => navigate("/profiles/new")}
          disabled={data.profiles_used >= data.max_profiles}
          data-testid="new-profile-btn"
        >
          <Plus className="w-4 h-4" /> New
        </button>
      </div>

      {data.profiles.length === 0 ? (
        <EmptyState
          icon={LayoutGrid}
          title="No profiles yet"
          description="Create a monitoring profile to start tracking Price Fixing Unit maximum retail prices for the products you care about."
          testId="dashboard-empty"
          action={
            <button className="btn-primary" onClick={() => navigate("/profiles/new")} data-testid="empty-new-profile">
              <Plus className="w-4 h-4" /> Create your first profile
            </button>
          }
        />
      ) : (
        <div className="space-y-3">
          {(data.profiles || []).map((p) => (
            <div key={p.id} className="card p-4" data-testid={`profile-card-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <button className="text-left flex-1 min-w-0" onClick={() => navigate(`/profiles/${p.id}`)} data-testid={`profile-open-${p.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-display text-lg text-ink truncate">{p.name}</h3>
                  </div>
                  <div className="chip bg-teal-light text-teal">{p.catalogue_label}</div>
                </button>
                <ChevronRight className="w-5 h-5 text-muted shrink-0" />
              </div>

              <div className="grid grid-cols-2 gap-y-2 gap-x-4 mt-4 text-sm">
                <div className="flex items-center gap-2 text-muted">
                  <Package className="w-4 h-4" /> {p.product_count} products
                </div>
                <div className="flex items-center gap-2 text-muted">
                  <CalendarClock className="w-4 h-4" /> {scheduleText(p)}
                </div>
                <div className="flex items-center gap-2 text-muted">
                  Last scan:
                  {p.last_scan ? <StatusBadge status={p.last_scan.status} /> : <span>never</span>}
                </div>
                <div className="flex items-center gap-2 text-muted">
                  Last changes: <span className="text-ink font-medium">{p.last_change_count}</span>
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  className="btn-primary flex-1"
                  onClick={() => runNow(p.id)}
                  disabled={running[p.id]}
                  data-testid={`run-now-${p.id}`}
                >
                  {running[p.id] ? <Spinner /> : <Play className="w-4 h-4" />} Run now
                </button>
                <button
                  className="btn-ghost flex-1"
                  onClick={() => navigate(`/profiles/${p.id}`)}
                  data-testid={`open-profile-${p.id}`}
                >
                  Open
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
